import React, { useEffect } from "react";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles } from "@material-ui/core/styles";
import useInterval from "../../core-module/hook/useInterval";
import { useConnect, useSchema } from "../../core-module/hook/connectingHooks";
import { useSelector } from "react-redux";

export const PathContext = React.createContext({});

function findDropdownDisplay(propName, propValue, schema) {
  if (schema == null || schema[propName] == null) {
    return "";
  }

  if (!("dropdownOptions" in schema[propName])) {
    return "";
    throw new Error(
      "Prop [" +
        propName +
        "] is not dropdown type or is missing dropdownOptions"
    );
  }

  for (const option of schema[propName].dropdownOptions) {
    if (String(option[0]) === String(propValue)) {
      if (option[1] != null) {
        return option[1];
      }
      return option[0];
    }
  }
  return propValue || "";
}

export function DropdownDisplay({ propName, propValue }) {
  const path = React.useContext(PathContext);
  const bigSchema = useSchema(path);

  return findDropdownDisplay(propName, propValue, bigSchema.props);
}

const useLoadingStyles = makeStyles((theme) => ({
  loadingroot: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    "& > * + *": {
      marginLeft: theme.spacing(2),
    },
  },
}));

function Loading() {
  const classes = useLoadingStyles();
  return (
    <div className={classes.loadingroot}>
      <CircularProgress />
    </div>
  );
}

export function paramsFromLocation() {
  const pathname = window.location.pathname;
  const params = {};
  if (pathname == null) {
    return params;
  }
  const pathparts = pathname.split("/");
  for (let key in pathparts) {
    if (Number(key) === 0) {
      continue;
    }
    if (!Number.isNaN(Number(pathparts[key]))) {
      params[pathparts[key - 1]] = pathparts[key];
    }
  }
  return params;
}

export function connectedComponent(InnerComponent, args) {
  const ReturnComponent = function WrapperComponent(props, ref) {
    const user = useSelector((state) => state.services.ids.user);
    const additionalHttpGetParameters = args?.httpGetParameters
      ? "&" +
        args?.httpGetParameters
          .filter(
            (paramName) =>
              props[paramName] != null && String(props[paramName]) !== ""
          )
          .map(
            (paramName) =>
              paramName + "=" + encodeURIComponent(props[paramName])
          )
          .join("&")
      : props.httpGetParameters
      ? "&" +
        props.httpGetParameters
          .filter(
            (paramName) =>
              props[paramName] != null && String(props[paramName]) !== ""
          )
          .map(
            (paramName) =>
              paramName + "=" + encodeURIComponent(props[paramName])
          )
          .join("&")
      : "";

    //ambigousProps trick when propName=varname
    const { loading, ...ambiguousProps } = useConnect(
      args?.path != null ? args.path : props.path,
      { ...paramsFromLocation(), ...props.pathids, user },
      // redux stores => services ids
      additionalHttpGetParameters,
      args?.activeSubscription != null
        ? args.activeSubscription
        : props.path != null
        ? props.activeSubscription
        : false
    );

    //passThroughProps is important when nesting connectedComponents!!!
    const {
      dataPath,
      refresh,
      data,
      onDataChange,
      refreshInterval,
      showDataNotAvailable,
      loadingComponent,
      waitForDataBeforeRender,
      ...passThroughProps
    } = props;

    useInterval(() => {
      if (
        (args?.refreshInterval || props?.refreshInterval) &&
        typeof ambiguousProps.refresh === "function"
      ) {
        ambiguousProps.refresh();
      }
    }, [args?.refreshInterval || props?.refreshInterval || 10000]);

    const dataToInnerComponent = ambiguousProps.data;
    const refreshToInnerComponent = ambiguousProps.refresh;
    const pathToInnerComponent = ambiguousProps.path;

    useEffect(() => {
      if (onDataChange != null) {
        onDataChange(dataToInnerComponent);
      }
    }, [dataToInnerComponent, onDataChange]);

    useEffect(() => {
      const onRefreshAll = () => {
        refreshToInnerComponent();
      };
      window.addEventListener("refreshAllConnects", onRefreshAll);
      return () =>
        window.removeEventListener("refreshAllConnects", onRefreshAll);
    }, [refreshToInnerComponent]);

    let content = null;

    if (
      dataToInnerComponent == null &&
      waitForDataBeforeRender !== false &&
      args?.waitForDataBeforeRender !== false
    ) {
      if (loading) {
        const myLoadingComponent = args?.loadingComponent || loadingComponent;
        if (myLoadingComponent === true) {
          content = <Loading />;
        } else if (myLoadingComponent) {
          content = myLoadingComponent;
        }
      } else {
        content = props.showDataNotAvailable ? (
          <Typography>Data Not Available</Typography>
        ) : null;
      }
    } else {
      content = (
        <InnerComponent
          {...passThroughProps}
          dataPath={pathToInnerComponent}
          refresh={refreshToInnerComponent}
          data={dataToInnerComponent}
        />
      );
    }

    return (
      <PathContext.Provider value={pathToInnerComponent}>
        {content}
      </PathContext.Provider>
    );
  };
  return React.forwardRef(ReturnComponent);
}

export default {};
